import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Edit, Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { MRT_ColumnDef, MRT_SortingState } from "material-react-table";
import { format } from "date-fns";

import { supabase } from "../../utils/API";
import {
  useGetMoviesQuery,
  useUpdateMovieMutation,
} from "../../services/movies.query";

import { DefaultPage } from "../../layouts/default-page";
import { Notify } from "../../layouts/notify";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { RowActions } from "../../components/common/RowActions";

type MovieRow = {
  id: string | number;
  mtrcb_application_no?: string | null;
  avatar_img?: string | null;
  new_poster_url?: string | null;
  title?: string | null;
  genre?: string | null;
  category?: number | string | null;
  date_approved_last?: string | null;
};

const ROWS_PER_PAGE = 10;

const getMoviePosterUrl = (row: MovieRow) => {
  if (row.new_poster_url) {
    if (row.new_poster_url.startsWith("http")) {
      return row.new_poster_url;
    }
    return `https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/movie-posters/${row.new_poster_url}`;
  }
  return `http://midas.mtrcb.gov.ph/etransact/assets/uploads/pm/${row.avatar_img || "pm.png"}`;
};

export const MovieTest = () => {
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [page, setPage] = useState(1);
  const [draftSearch, setDraftSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "date_approved_last", desc: true },
  ]);

  const [posterDialogueState, setPosterDialogueState] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MovieRow | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [movieUpdate, { isLoading: movieUpdateLoading }] = useUpdateMovieMutation();
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenPosterDialogue = (rowData: MovieRow) => {
    setSelectedItem(rowData);
    setPosterFile(null);
    setPosterDialogueState(true);
  };

  const handleClosePosterDialogue = () => {
    setPosterDialogueState(false);
    setSelectedItem(null);
    setPosterFile(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.currentTarget.files) {
      const file = e.currentTarget.files[0];
      if (file.size > 150 * 1024) {
        Notify("error", "Poster image exceeds 150KB maximum limit");
        e.target.value = "";
        return;
      }
      setPosterFile(file);
    }
  };

  const handleUploadPoster = async () => {
    if (!selectedItem || !posterFile) return;

    try {
      setIsUploading(true);
      const ext = posterFile.name.split(".").pop()?.toLowerCase() || "jpg";
      const filename = `${crypto.randomUUID()}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("movie-posters")
        .upload(`movies/${filename}`, posterFile, {
          upsert: true,
          contentType: posterFile.type || "image/jpeg",
        });

      if (uploadErr) throw uploadErr;

      const fullUrl = `https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/movie-posters/movies/${filename}`;

      await movieUpdate({
        id: selectedItem.id,
        new_poster_url: fullUrl,
      }).unwrap();

      Notify("info", "Movie poster updated successfully");
      handleClosePosterDialogue();
    } catch (error) {
      console.error("Error during upload:", error);
      Notify("error", "Failed to update movie poster");
    } finally {
      setIsUploading(false);
    }
  };

  const {
    data: moviesResponse,
    isLoading,
    isFetching,
    isError,
  } = useGetMoviesQuery({
    year: selectedYear,
    title: searchQuery,
    page,
    rowsPerPage: ROWS_PER_PAGE,
    sortColumn: sorting.length > 0 ? sorting[0].id : "date_approved_last",
    sortDirection: sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "desc",
  });

  const movies = moviesResponse?.list ?? [];
  const count = moviesResponse?.count ?? 0;

  const yearOptions = useMemo(() => {
    const startYear = 2000;
    const endYear = currentYear + 1;
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, index) => endYear - index
    );
  }, [currentYear]);

  const columns = useMemo<MRT_ColumnDef<MovieRow>[]>(
    () => [
      {
        accessorKey: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }) => (
          <Typography>{(page - 1) * ROWS_PER_PAGE + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "mtrcb_application_no",
        header: "APPLICATION NO.",
        size: 180,
      },
      {
        accessorKey: "avatar_img",
        header: "MOVIE POSTER",
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => {
          const posterUrl = getMoviePosterUrl(row.original);
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                alt={row.original.title || "Movie poster"}
                src={posterUrl}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: 4,
                  cursor: "pointer",
                  border: "1px solid #E2E8F0",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(posterUrl, "_blank");
                }}
              />
            </Box>
          );
        },
      },
      {
        accessorKey: "title",
        header: "TITLE",
        size: 300,
        Cell: ({ row }) => (
          <Typography fontWeight={600} fontSize={13.5} color="#0F172A">
            {row.original.title || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "genre",
        header: "GENRE",
        size: 180,
        Cell: ({ row }) => (
          <Typography fontSize={13} color="#475569">
            {row.original.genre || "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "category",
        header: "RATING",
        size: 120,
        Cell: ({ row }) => {
          const category = Number(row.original.category);

          let str = "G";
          let backgroundColor = "#16A34A";

          if (category === 1) {
            backgroundColor = "#16A34A";
            str = "G";
          } else if (category === 2) {
            backgroundColor = "#0284C7";
            str = "PG";
          } else if (category === 3) {
            backgroundColor = "#D97706";
            str = "R-13";
          } else if (category === 4) {
            backgroundColor = "#EA580C";
            str = "R-16";
          } else if (category === 5) {
            backgroundColor = "#DC2626";
            str = "R-18";
          }

          return (
            <Box
              sx={{
                backgroundColor,
                borderRadius: "3px",
                px: 1,
                py: 0.25,
                display: "inline-block",
                textAlign: "center",
                minWidth: 44,
              }}
            >
              <Typography fontWeight={700} fontSize={11.5} sx={{ color: "#FFFFFF" }}>
                {str}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "date_approved_last",
        header: "RELEASE DATE",
        size: 160,
        Cell: ({ row }) => {
          const value = row.original.date_approved_last;
          if (!value) return <Typography fontSize={13} color="#94A3B8">-</Typography>;
          return (
            <Typography fontSize={13} color="#475569">
              {format(new Date(value), "MMM dd, yyyy")}
            </Typography>
          );
        },
      },
      {
        id: "actions",
        header: "ACTIONS",
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => (
          <RowActions
            actions={[
              {
                label: "Edit Poster",
                onClick: () => handleOpenPosterDialogue(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page]
  );

  const handleSearch = () => {
    setSearchQuery(draftSearch);
    setPage(1);
  };

  const handleClear = () => {
    setDraftSearch("");
    setSearchQuery("");
    setSelectedYear(currentYear);
    setPage(1);
  };

  return (
    <>
      <DefaultPage 
        title="Movies"
        subTitle="Manage movie master records, application numbers, posters, and ratings."
        loading={isLoading}
      >
        <Box mb={3}>
          <Alert severity="info">
            Displays registered movies from the system. Set the filters and click <strong>Search</strong> to load results.
          </Alert>
        </Box>

        {isError && (
          <Box mb={3}>
            <Alert severity="error">
              Failed to load movies.
            </Alert>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2} mb={3}>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="movie-year-label">Year</InputLabel>
              <Select
                labelId="movie-year-label"
                value={selectedYear}
                label="Year"
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value));
                  setPage(1);
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search Title"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </Box>
          
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={handleSearch} disabled={isFetching}>
              {isFetching ? "Searching..." : "Search"}
            </Button>
            <Button variant="outlined" color="inherit" onClick={handleClear} disabled={isFetching}>
              Clear
            </Button>
          </Box>
        </Box>

        <Box mb={2}>
          <Alert severity="success">
            Total matched records: <strong>{Number(count || 0).toLocaleString()}</strong>
          </Alert>
        </Box>

        <DataTableFive<MovieRow>
          total={count}
          columns={columns}
          data={movies}
          page={page}
          pageSize={ROWS_PER_PAGE}
          onPageChange={setPage}
          getRowId={(row: MovieRow) => String(row.id)}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={(row: any) => handleOpenPosterDialogue(row.original)}
          sorting={sorting}
          onSortingChange={(newSorting) => {
            setSorting(newSorting);
            setPage(1);
          }}
        />
      </DefaultPage>

      <Dialog
        open={posterDialogueState}
        onClose={handleClosePosterDialogue}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <Paper elevation={0} sx={{ p: 3, position: "relative" }}>
          <IconButton
            aria-label="close"
            onClick={handleClosePosterDialogue}
            sx={{ position: "absolute", top: 12, right: 12, color: "#94A3B8" }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight={700} color="#0F172A" mb={0.5}>
            Upload Movie Poster
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {selectedItem?.title ? `Poster configuration for "${selectedItem.title}". ` : ""}
            Please select an image file to upload as the new poster. Maximum file size is 150KB.
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="poster-upload-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="poster-upload-input" style={{ width: "100%" }}>
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{
                  height: 100,
                  borderStyle: "dashed",
                  borderRadius: "4px",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {posterFile ? posterFile.name : "Click to select file (max 150KB)"}
              </Button>
            </label>
            <LoadingButton
              variant="contained"
              onClick={handleUploadPoster}
              loading={isUploading || movieUpdateLoading}
              disabled={!posterFile}
              fullWidth
              sx={{
                borderRadius: "4px",
                bgcolor: "#0072BC",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "#005a96" },
              }}
            >
              Upload and Save
            </LoadingButton>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
};